import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { AuthProvider } from "./context/AuthContext";
import { DashboardProvider } from "./context/DashboardContext";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import AuthCallback from "./components/auth/AuthCallback";
import Sidebar from "./components/layout/Sidebar";
import TopHeader from "./components/layout/TopHeader";
import BottomTabBar from "./components/layout/BottomTabBar";
import { Toaster } from "./components/ui/sonner";

import Login from "./pages/Login";
import CommandCenter from "./pages/CommandCenter";
import Watchlist from "./pages/Watchlist";
import MarketScanner from "./pages/MarketScanner";
import ChartAnalysis from "./pages/ChartAnalysis";
import TradePlanner from "./pages/TradePlanner";
import OpenPlans from "./pages/OpenPlans";
import MarketIntelligence from "./pages/MarketIntelligence";
import EconomicCalendar from "./pages/EconomicCalendar";
import RiskCenter from "./pages/RiskCenter";
import PaperTrading from "./pages/PaperTrading";
import TradingJournal from "./pages/TradingJournal";
import Performance from "./pages/Performance";
import Alerts from "./pages/Alerts";
import Settings from "./pages/Settings";
import AccessControl from "./pages/AccessControl";

function Layout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  return (
    <div className="App min-h-screen" style={{ background: "var(--bg)" }}>
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <div className={`transition-[margin] duration-200 ${collapsed ? "md:ml-[68px]" : "md:ml-[236px]"}`}>
        <TopHeader onMenu={() => setMobileOpen(true)} />
        <main className="px-3 sm:px-5 py-4 sm:py-5 pb-20 md:pb-5">{children}</main>
      </div>
      <BottomTabBar />
    </div>
  );
}

function Dashboard() {
  return (
    <DashboardProvider>
      <Layout>
        <Routes>
          <Route path="/" element={<CommandCenter />} />
          <Route path="/watchlist" element={<Watchlist />} />
          <Route path="/scanner" element={<MarketScanner />} />
          <Route path="/chart" element={<ChartAnalysis />} />
          <Route path="/planner" element={<TradePlanner />} />
          <Route path="/plans" element={<OpenPlans />} />
          <Route path="/intelligence" element={<MarketIntelligence />} />
          <Route path="/calendar" element={<EconomicCalendar />} />
          <Route path="/risk" element={<RiskCenter />} />
          <Route path="/paper" element={<PaperTrading />} />
          <Route path="/journal" element={<TradingJournal />} />
          <Route path="/performance" element={<Performance />} />
          <Route path="/alerts" element={<Alerts />} />
          <Route path="/access" element={<AccessControl />} />
          <Route path="/settings" element={<Settings />} />
        </Routes>
      </Layout>
    </DashboardProvider>
  );
}

function AppRouter() {
  const location = useLocation(); // read hash from here, not window.location.hash (not reactive)
  // Process OAuth callback FIRST, synchronously during render, before any auth check.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/*" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
    </Routes>
  );
}

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function App() {
  const tree = (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" theme="dark" />
    </AuthProvider>
  );
  // Only wrap with Google provider when a Client ID is configured (keeps the
  // legacy flow working before the ID is set). GIS needs the public Client ID.
  return GOOGLE_CLIENT_ID
    ? <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>{tree}</GoogleOAuthProvider>
    : tree;
}

export default App;
